import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppContext } from '../../context/AppContext';
import { getPassTypes, purchasePass } from '../../services/passService';
import { getPolicy } from '../../services/policyService';
import { getUser, updateUser } from '../../services/userService';
import { ScheduleTrans, IndividualTrans } from '../../utils/translations';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { isIndividualPass } from '../../utils/passHelpers';
import Navbar from '../landing/Navbar';
import AboutSection from '../landing/AboutSection';
import IndividualPricingSection from './IndividualPricingSection';
import PolicyModal from '../Schedule/PolicyModal';
import '../../styles/landing.css';
import '../../styles/individual-landing.css';

export default function IndividualClasses() {
  const { language, user, token, setUser, setToaster, setLoading } = useAppContext();
  const { loginWithRedirect } = useAuth0();

  const forWhomRef = useRef(null);
  const approachRef = useRef(null);
  const aboutRef = useRef(null);
  const pricesRef = useRef(null);
  const ctaRef = useRef(null);

  const [passTypes, setPassTypes] = useState([]);
  const [loading, setLoadingLocal] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [rulesAgreedChecked, setRulesAgreedChecked] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const [forWhomRevealRef, forWhomVisible] = useScrollReveal();
  const [approachRevealRef, approachVisible] = useScrollReveal();
  const [reviewRevealRef, reviewVisible] = useScrollReveal();
  const [formatRevealRef, formatVisible] = useScrollReveal();
  const [pricingRevealRef, pricingVisible] = useScrollReveal();

  const t = IndividualTrans[language];
  const details = ScheduleTrans[language];
  const policy = user?.policyAgreements;
  const showRulesCheckbox = !!(
    user &&
    (!policy?.agreedAt || (currentPolicy && policy?.version !== currentPolicy.version))
  );
  const policyTitle = details.firstPassRulesTitle;
  const policyContent = currentPolicy
    ? (language === 'russian' ? currentPolicy.contentRu : currentPolicy.contentEn) || details.firstPassRulesContent
    : details.firstPassRulesContent;

  useEffect(() => {
    let cancelled = false;
    getPassTypes({ all: true })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const individual = list
          .filter((p) => isIndividualPass(p))
          .sort((a, b) => (a.classesIncluded || 0) - (b.classesIncluded || 0));
        if (!cancelled) setPassTypes(individual);
      })
      .catch(() => {
        if (!cancelled) setPassTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLocal(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    getPolicy().then(setCurrentPolicy).catch(() => {});
  }, []);

  const refs = { practice: forWhomRef, formats: approachRef, prices: pricesRef, about: aboutRef, contacts: ctaRef };
  const scrollToRef = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth' });
  const handleSignUp = () => scrollToRef(pricesRef);

  const sessionLabel = (n) => {
    if (n === 1) return t.sessionOne;
    if (language === 'russian' && n >= 2 && n <= 4) return t.sessionFew;
    return t.sessionMany;
  };

  const handlePurchase = async (passType) => {
    if (!user || !token) {
      window.localStorage.setItem('redirectTo', '/individual');
      loginWithRedirect({ ui_locales: language === 'russian' ? 'ru' : 'en' });
      return;
    }
    if (showRulesCheckbox && !rulesAgreedChecked) {
      setToaster({ message: details.firstPassRulesAgreementRequired, type: 'error', show: true });
      return;
    }
    setPurchasingId(passType._id);
    try {
      setLoading(true);
      if (showRulesCheckbox && rulesAgreedChecked) {
        await updateUser(token, {
          policyAgreements: {
            ...user?.policyAgreements,
            agreedAt: new Date().toISOString(),
            ...(currentPolicy?.version != null && { version: currentPolicy.version }),
          },
        });
        const updatedUser = await getUser(token);
        setUser(updatedUser);
      }
      const response = await purchasePass(passType._id, token, false, null);
      if (response?.paymentUrl) {
        window.location.href = response.paymentUrl;
        return;
      }
      setToaster({ message: details.bookingError || 'Failed to create payment', type: 'error', show: true });
    } catch (err) {
      setToaster({ message: details.bookingError || 'Failed to create payment', type: 'error', show: true });
    } finally {
      setLoading(false);
      setPurchasingId(null);
    }
  };

  const singlePrice = passTypes.find((p) => p.classesIncluded === 1)?.basePrice ?? 230;

  const requestItems = [
    { icon: t.requests1Icon, text: t.requests1Text },
    { icon: t.requests2Icon, text: t.requests2Text },
    { icon: t.requests3Icon, text: t.requests3Text },
    { icon: t.requests4Icon, text: t.requests4Text },
  ];

  return (
    <main className="landing-page min-h-screen scroll-smooth font-sans antialiased bg-warm-white">
      <Navbar scrollToRef={scrollToRef} refs={refs} onSignUp={handleSignUp} />

      {/* Hero */}
      <section className="relative bg-cream pt-40 pb-28 md:pt-[160px] md:pb-32 px-6 md:px-20 overflow-hidden ind-grain">
        <div className="ind-blob w-[min(520px,90vw)] h-[min(520px,90vw)] -top-20 -right-24 bg-clay" />
        <div className="ind-blob w-80 h-80 bottom-10 left-[-4rem] bg-sage-light ind-float-slow" />
        <div className="absolute top-1/3 right-[12%] w-3 h-3 rounded-full bg-clay/40 ind-float-delay hidden lg:block" aria-hidden />
        <div className="max-w-[760px] relative z-10">
          <div className="animate-fade-up flex items-center gap-3 text-eyebrow font-sans font-normal uppercase tracking-[0.18em] text-clay mb-7" style={{ animationDelay: '0.08s' }}>
            <span className="w-10 h-px bg-gradient-to-r from-clay to-transparent flex-shrink-0" />
            {t.eyebrow}
          </div>
          <h1 className="font-serif font-medium leading-[1.02] text-ink mb-8 animate-fade-up" style={{ fontSize: 'clamp(44px, 7vw, 84px)', animationDelay: '0.15s' }}>
            {t.heroTitle}
            <br />
            <span className="inline-block mt-1">
              <em className="italic text-clay font-serif">{t.heroTitleEm}</em>
              {t.heroTitleSuffix}
            </span>
          </h1>
          <p className="text-lg md:text-xl font-sans font-light leading-relaxed text-ink-mid max-w-[560px] mb-10 animate-fade-up border-l-2 border-clay/40 pl-6" style={{ animationDelay: '0.28s' }}>
            {t.heroSub}
          </p>
          <div
            className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 mb-14 animate-fade-up w-full min-w-0"
            style={{ animationDelay: '0.4s' }}
          >
            {[t.badge1, t.badge2, t.badge3].map((label, i) => (
              <div
                key={i}
                className="inline-flex w-full sm:w-auto sm:max-w-[min(100%,20rem)] md:max-w-none max-w-full min-w-0 items-center gap-2.5 text-xs sm:text-sm font-sans font-normal text-ink bg-white/80 backdrop-blur-sm border border-sand-dark/80 py-2 px-4 sm:py-2.5 sm:px-5 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 md:rounded-full"
              >
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-clay to-clay-dark flex-shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 sm:flex-initial leading-snug break-words">{label}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] font-sans uppercase tracking-[0.2em] text-ink-light/80 animate-fade-up" style={{ animationDelay: '0.55s' }}>
            {t.heroScroll} ↓
          </p>
        </div>
      </section>

      {/* For whom */}
      <section ref={forWhomRef} className="py-24 md:py-32 px-6 md:px-20 bg-warm-white relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div
          ref={forWhomRevealRef}
          className={`max-w-5xl mx-auto transition-all duration-700 ${forWhomVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="text-xs font-sans uppercase tracking-[0.14em] text-clay/90 mb-3 md:visible hidden">{t.forWhomKicker}</p>
          <div className="flex items-center gap-3 text-eyebrow font-sans uppercase tracking-[0.18em] text-clay mb-4">
            <span className="w-6 h-px bg-clay flex-shrink-0" />
            {t.forWhomLabel}
          </div>
          <h2 className="font-serif font-medium leading-tight text-ink mb-16 max-w-3xl" style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}>
            {t.forWhomTitle}
            <em className="italic text-clay">{t.forWhomTitleEm}</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              { num: t.forWhom1Num, title: t.forWhom1Title, text: t.forWhom1Text },
              { num: t.forWhom2Num, title: t.forWhom2Title, text: t.forWhom2Text },
              { num: t.forWhom3Num, title: t.forWhom3Title, text: t.forWhom3Text },
              { num: t.forWhom4Num, title: t.forWhom4Title, text: t.forWhom4Text },
            ].map((card, i) => (
              <article
                key={i}
                className="group relative bg-white border border-sand-dark/60 rounded-2xl p-8 md:p-10 shadow-[0_1px_0_rgba(42,36,32,0.04)] hover:shadow-xl hover:border-clay/25 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <span className="absolute -right-2 -top-4 font-serif text-[7rem] leading-none text-clay/[0.07] select-none pointer-events-none group-hover:text-clay/[0.11] transition-colors">
                  {card.num}
                </span>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 text-xs font-sans font-medium tracking-wider text-clay mb-4">
                    <span className="w-8 h-px bg-clay/60" />
                    {card.num}
                  </div>
                  <h3 className="font-serif text-xl md:text-[22px] text-center font-medium text-ink mb-3 leading-snug">{card.title}</h3>
                  <p className="text-sm md:text-[15px] font-sans font-light leading-relaxed text-ink-mid">{card.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section ref={approachRef} className="py-24 md:py-32 px-6 md:px-20 bg-warm-white">
        <div
          ref={approachRevealRef}
          className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start transition-all duration-700 ${approachVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="lg:sticky lg:top-28">
            <div className="flex items-center gap-3 text-eyebrow font-sans uppercase tracking-[0.18em] text-clay mb-5">
              <span className="w-6 h-px bg-clay flex-shrink-0" />
              {t.approachLabel}
            </div>
            <h2 className="font-serif font-medium leading-tight text-ink mb-6" style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}>
              {t.approachTitle}
              <em className="italic text-clay">{t.approachTitleEm}</em>
              {t.approachTitleSuffix}
            </h2>
            <p className="text-[17px] font-sans font-light leading-relaxed text-ink-mid">
              {t.approachLead}
            </p>
            <div className="mt-10 relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-clay/20 via-transparent to-sage-light/20 rounded-2xl blur-sm" />
              <blockquote className="relative font-serif text-lg md:text-xl italic text-ink leading-relaxed p-8 md:p-9 bg-white border border-sand-dark/40 rounded-2xl shadow-sm -rotate-[0.35deg]">
                {t.approachQuote}
              </blockquote>
            </div>
          </div>
          <div className="flex flex-col gap-0">
            {[
              { icon: t.approach1Icon, title: t.approach1Title, text: t.approach1Text },
              { icon: t.approach2Icon, title: t.approach2Title, text: t.approach2Text },
              { icon: t.approach3Icon, title: t.approach3Title, text: t.approach3Text },
              { icon: t.approach4Icon, title: t.approach4Title, text: t.approach4Text },
            ].map((item, i) => (
              <div key={i} className="group flex gap-5 py-8 border-b border-sand-dark/70 first:border-t first:pt-0 first:border-t-0 first:pb-8">
                <span className="font-serif text-sm text-clay flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-cream group-hover:bg-clay/10 transition-colors">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="font-serif text-lg font-medium text-ink mb-2 leading-snug">{item.title}</h3>
                  <p className="text-sm text-left md:text-[15px] font-sans font-light leading-relaxed text-ink-mid">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student review */}
      <section className="py-24 md:py-32 px-6 md:px-20 bg-cream relative overflow-hidden ind-grain">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div
          ref={reviewRevealRef}
          className={`max-w-3xl mx-auto transition-all duration-700 ${reviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex items-center gap-3 text-eyebrow font-sans uppercase tracking-[0.18em] text-clay mb-8">
            <span className="w-6 h-px bg-clay flex-shrink-0" />
            {t.reviewLabel}
          </div>
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-clay/15 via-transparent to-sage-light/15 rounded-2xl blur-sm" />
            <div className="relative bg-white/90 backdrop-blur-sm border border-sand-dark/50 rounded-2xl p-8 md:p-12 shadow-sm">
              <div className="space-y-5 text-[15px] md:text-[17px] font-sans font-light leading-relaxed text-ink-mid">
                {t.reviewParagraphs.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <p className="mt-10 font-serif text-lg text-ink text-right">— {t.reviewAuthor}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About teacher — same block as landing */}
      <div ref={aboutRef}>
        <AboutSection />
      </div>

      {/* Format strip */}
      <div
        ref={formatRevealRef}
        className={`grid grid-cols-1 md:grid-cols-2 gap-px bg-brown/90 shadow-inner transition-all duration-700 ${formatVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <div className="bg-brown p-10 md:p-14 hover:bg-[#5c422f] transition-all duration-300 md:border-r border-white/10 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/[0.03] rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-500" />
          <div className="relative">
            <div className="flex items-center gap-2.5 text-[11px] font-sans uppercase tracking-[0.18em] text-clay-light mb-5">
              <span className="w-5 h-px bg-clay-light flex-shrink-0" />
              {t.formatInPersonLabel}
            </div>
            <h3 className="font-serif text-2xl md:text-[30px] font-medium text-white mb-4 leading-tight">{t.formatInPersonTitle}</h3>
            <p className="text-sm font-sans font-light leading-relaxed text-white/75 max-w-md">{t.formatInPersonText}</p>
          </div>
        </div>
        <div className="bg-brown p-10 md:p-14 hover:bg-[#5c422f] transition-all duration-300 border-t md:border-t-0 md:border-l border-white/10 relative overflow-hidden group">
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/[0.03] rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-110 transition-transform duration-500" />
          <div className="relative">
            <div className="flex items-center gap-2.5 text-[11px] font-sans uppercase tracking-[0.18em] text-clay-light mb-5">
              <span className="w-5 h-px bg-clay-light flex-shrink-0" />
              {t.formatOnlineLabel}
            </div>
            <h3 className="font-serif text-2xl md:text-[30px] font-medium text-white mb-4 leading-tight">{t.formatOnlineTitle}</h3>
            <p className="text-sm font-sans font-light leading-relaxed text-white/75 max-w-md">{t.formatOnlineText}</p>
          </div>
        </div>
      </div>

      <IndividualPricingSection
        sectionRef={pricesRef}
        revealRef={pricingRevealRef}
        visible={pricingVisible}
        passTypes={passTypes}
        loading={loading}
        singlePrice={singlePrice}
        showRulesCheckbox={showRulesCheckbox}
        rulesAgreedChecked={rulesAgreedChecked}
        setRulesAgreedChecked={setRulesAgreedChecked}
        onOpenPolicy={() => setShowPolicyModal(true)}
        purchasingId={purchasingId}
        onPurchase={handlePurchase}
        user={user}
        details={details}
        t={t}
        sessionLabel={sessionLabel}
      />

      <PolicyModal open={showPolicyModal} onClose={() => setShowPolicyModal(false)} title={policyTitle} content={policyContent} />
    </main>
  );
}
